import { ChangeDetectionStrategy, Component, effect, input, model, output } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { CheckboxComponent, DatepickerComponent, FieldLabelComponent } from 'nebula-ui-kit';

/**
 * Fecha con modo automatico: la etiqueta, el calendario y el interruptor de "automatico"
 * como una sola pieza.
 *
 * <p>Nace de Parametros de Contabilidad, donde los tres elementos vivian sueltos en la
 * plantilla y cada pantalla que necesitaba lo mismo tenia que volver a cablearlos. Toda
 * opcion de parametros del ERP resuelve la fecha de operacion con esta misma terna.</p>
 *
 * <p>Lleva SOLO las reglas genericas de la terna: en automatico el campo se apaga, pierde el
 * valor y deja de ser obligatorio; en manual se enciende y pasa a obligatorio; y elegir una
 * fecha apaga el automatico por si mismo. Todo lo demas —contra que se valida la fecha, que
 * mensaje se muestra, con que formato viaja al backend— es del negocio de cada pantalla y
 * sale por los eventos, porque la regla de contabilidad no es la de facturacion.</p>
 *
 * <p>Para rechazar una fecha, la pantalla escribe en el control y en `automatic`: ambos son
 * suyos, de modo que devolver el estado anterior no necesita ningun metodo del componente.</p>
 */
@Component({
  standalone: true,
  selector: 'app-auto-date-picker',
  imports: [ReactiveFormsModule, DatepickerComponent, CheckboxComponent, FieldLabelComponent],
  templateUrl: './auto-date-picker.component.html',
  styleUrls: ['./auto-date-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutoDatePickerComponent {
  /** Control de la fecha dentro del formulario de la pantalla. */
  readonly control = input.required<FormControl>();

  readonly label = input<string>('');
  readonly tooltip = input<string>('');
  readonly automaticLabel = input<string>('Automático');

  /** Texto del campo mientras la fecha la pone el sistema. */
  readonly automaticPlaceholder = input<string>('Fecha automática');
  readonly manualPlaceholder = input<string>('Seleccione una fecha');
  readonly requiredMsg = input<string>('');

  readonly minDate = input<Date | undefined>(undefined);
  readonly maxDate = input<Date | undefined>(undefined);

  /**
   * Modo automatico. Es bidireccional a proposito: la pantalla necesita poder apagarlo
   * —o volver a encenderlo— cuando su propia regla rechaza la fecha que se eligio.
   */
  readonly automatic = model<boolean>(true);

  /** Fecha elegida en el calendario. La pantalla decide si la acepta. */
  readonly dateChange = output<Date | null>();

  constructor() {
    // Un solo sitio decide como queda el control segun el modo. Se escribe sin emitir para
    // no despertar los `valueChanges` que la pantalla ya tiene enganchados a su formulario.
    effect(() => {
      const automatico = this.automatic();
      const control = this.control();

      if (automatico) {
        control.clearValidators();
        control.setValue(null, { emitEvent: false });
        control.disable({ emitEvent: false });
      } else {
        control.setValidators([Validators.required]);
        control.enable({ emitEvent: false });
      }

      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  /** Marca del interruptor de automatico. */
  alternarAutomatico(automatico: boolean): void {
    this.automatic.set(automatico);
  }

  /**
   * Fecha elegida en el calendario.
   *
   * <p>Elegir una fecha es, por si mismo, salir del modo automatico: quien abre el
   * calendario y escoge un dia esta diciendo que la quiere poner a mano.</p>
   */
  alFijarFecha(fecha: Date | null): void {
    if (fecha) {
      this.automatic.set(false);
    }
    this.dateChange.emit(fecha);
  }

  /** Cierto cuando hay que pintar el mensaje de campo obligatorio. */
  get faltaLaFecha(): boolean {
    const control = this.control();
    return !this.automatic() && !!control.touched && control.invalid;
  }
}
